import { FC, useMemo } from 'react';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { UnicodeEntity } from '@mews/enums';
import { lt, useLocalizeCurrency } from '@mews/framework';
import { Stack, Typography } from '@optimus-web/core';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { eqAmount } from '@mews-commander/utils';

interface CancellationFeeTextProps {
    reservationIds: string[];
    startUtc?: string;
    endUtc?: string;
}

export const CancellationFeeText: FC<CancellationFeeTextProps> = ({
    reservationIds,
    startUtc,
    endUtc,
}: CancellationFeeTextProps) => {
    const lc = useLocalizeCurrency();
    const canQuery = !!reservationIds.length && !!startUtc && !!endUtc;

    const { data } = useRepositoryQuery(
        'reservations',
        'getUpdateIntervalCancellationFee',
        {
            reservationIds,
            startUtc: startUtc as string,
            endUtc: endUtc as string,
        },
        {
            enabled: canQuery,
        }
    );

    const uniqueCancellationFees = useMemo(
        () =>
            pipe(
                data?.reservationCancellationFees || [],
                array.map((v) => v.cancellationFee),
                array.uniq(eqAmount)
            ),
        [data?.reservationCancellationFees]
    );
    const uniqueCancellationFee = uniqueCancellationFees.length === 1 ? uniqueCancellationFees[0] : null;

    return (
        <Stack>
            {lt('ApplyCancellationFee')}
            {uniqueCancellationFee && (
                <>
                    {UnicodeEntity.Colon}
                    {UnicodeEntity.NoBreakSpace}
                    <Typography textStyle="bodyMediumStrong" as="div">
                        {lc(uniqueCancellationFee.value, uniqueCancellationFee.currency)}
                    </Typography>
                </>
            )}
        </Stack>
    );
};
